import { cloneDeep, pick } from 'lodash-es'
import { Vector3 } from 'three'
import { EntityFactory } from '@/plugins/cad/module/CADModule/model/entity'
import type { BaseEntity, IEntity } from '@/plugins/cad/module/CADModule/model/entity/BaseEntity'
import { type ILayer, Layer } from '@/plugins/cad/module/CADModule/model/layer/Layer'
import type { IPoint3 } from '@/plugins/cad/module/common/interface'

/**
 * CAD 绘图平面的运行时描述，使用 Three.js 向量。
 */
export interface IPlane {
  /** 平面原点（基点） */
  origin: Vector3
  /** 平面法线 */
  normal: Vector3
  /** 平面上方向 */
  up: Vector3
  /** 平面相对于世界坐标的高程，单位米 */
  elevation: number
}

/**
 * CAD 绘图平面的可序列化描述，使用纯对象。
 */
export interface IPlaneMetaData {
  /** 平面原点（基点） */
  origin: IPoint3
  /** 平面法线 */
  normal: IPoint3
  /** 平面上方向 */
  up: IPoint3
  /** 平面相对于世界坐标的高程，单位米 */
  elevation: number
}

/**
 * CAD 图纸元数据，包含图层、实体与绘图平面。
 */
export interface ICADMetadata {
  /** 图层列表 */
  layers: ILayer[]
  /** 实体列表 */
  entities: IEntity[]
  /** 绘图平面元数据 */
  plane: IPlaneMetaData
}

/** 默认图层 id */
export const DefaultLayerId = 'default'

/**
 * 创建默认图层实例。
 *
 * @returns 默认图层
 */
export function createDefaultLayer(): Layer {
  const layer = new Layer({ id: DefaultLayerId, name: '默认图层1' })
  return layer
}

/**
 * CAD 数据管理器，负责维护图层、实体与绘图平面。
 */
export class DataManager {
  private layerMap: Map<string, Layer> = new Map()
  private entitieMap: Map<string, IEntity> = new Map()
  private _plane: IPlane = {
    origin: new Vector3(0, 0, 0),
    normal: new Vector3(0, 0, 1),
    up: new Vector3(0, 1, 0),
    elevation: 0,
  }

  /** 所有图层的数组快照 */
  get layers(): Layer[] {
    return Array.from(this.layerMap.values())
  }

  /** 所有实体的数组快照 */
  get entities(): IEntity[] {
    return Array.from(this.entitieMap.values())
  }

  /** 当前绘图平面 */
  get plane(): IPlane {
    return this._plane
  }

  /** 清空所有图层与实体。 */
  reset() {
    this.layerMap.clear()
    this.entitieMap.clear()
  }

  /**
   * 将当前图层、实体与绘图平面序列化为元数据。
   *
   * @returns 可持久化的图纸元数据
   */
  serialize(): ICADMetadata {
    // 序列化为 JSON
    const layers = Array.from(this.layerMap.values()).map((layer) => layer.serialize())
    const entities = Array.from(this.entitieMap.values()).map((entity) =>
      (entity as BaseEntity).serialize()
    )
    return {
      layers,
      entities,
      plane: {
        origin: pick(this.plane.origin, ['x', 'y', 'z']),
        normal: pick(this.plane.normal, ['x', 'y', 'z']),
        up: pick(this.plane.up, ['x', 'y', 'z']),
        elevation: this.plane.elevation,
      },
    }
  }

  /**
   * 从元数据构建 DataManager。
   *
   * @param data - 图纸元数据；为空时得到空白管理器
   * @returns 初始化好的 DataManager
   */
  static deserialize(data?: ICADMetadata) {
    const dataManager = new DataManager()
    const { layers = [], entities = [], plane } = data || {}
    // 反序列化 JSON 数据
    for (const layerData of layers) {
      const layer = new Layer(cloneDeep(layerData))
      dataManager.addLayer(layer)
    }
    for (const entityData of entities) {
      // 实体工厂函数根据类型创建实体实例
      const entity = EntityFactory.createEntity(cloneDeep(entityData))
      entity && dataManager.addEntity(entity)
    }
    if (plane) {
      dataManager.updatePlane(plane)
    }
    return dataManager
  }

  /**
   * 新增图层。
   *
   * @param layer - 待添加图层
   */
  addLayer(layer: Layer) {
    this.layerMap.set(layer.id, layer)
  }

  /**
   * 批量新增图层。
   *
   * @param layers - 待添加图层列表
   */
  addLayers(layers: Layer[]) {
    for (const layer of layers) {
      this.addLayer(layer)
    }
  }

  /**
   * 按 id 删除图层。
   *
   * @param id - 图层 id
   */
  deleteLayer(id: string) {
    this.layerMap.delete(id)
  }

  /**
   * 更新图层，按 id 覆盖同名图层。
   *
   * @param layer - 新图层数据
   */
  updateLayer(layer: Layer) {
    this.layerMap.set(layer.id, layer)
  }

  /**
   * 按 id 获取图层。
   *
   * @param id - 图层 id
   * @returns 图层；未找到返回 undefined
   */
  getLayer(id: string): Layer | undefined {
    return this.layerMap.get(id)
  }

  /**
   * 新增实体。
   *
   * @param entity - 待添加实体
   */
  addEntity(entity: IEntity) {
    this.entitieMap.set(entity.id, entity)
  }
  /**
   * 批量新增实体。
   *
   * @param entities - 待添加实体列表
   */
  addEntities(entities: IEntity[]) {
    for (const entity of entities) {
      this.addEntity(entity)
    }
  }

  /**
   * 删除实体。
   *
   * @param entity - 待删除实体，使用 id 定位
   */
  deleteEntity(entity: IEntity) {
    this.entitieMap.delete(entity.id)
  }
  /**
   * 批量删除实体。
   *
   * @param entities - 待删除实体列表
   */
  deleteEntities(entities: IEntity[]) {
    for (const entity of entities) {
      this.deleteEntity(entity)
    }
  }
  /** 清空所有实体。 */
  deleteAllEntities() {
    this.entitieMap.clear()
  }

  /**
   * 更新实体，按 id 覆盖同名实体。
   *
   * @param entity - 新实体数据
   */
  updateEntity(entity: IEntity) {
    this.entitieMap.set(entity.id, entity)
  }
  /**
   * 批量更新实体。
   *
   * @param entities - 新实体数据列表
   */
  updateEntities(entities: IEntity[]) {
    for (const entity of entities) {
      this.updateEntity(entity)
    }
  }

  /**
   * 按 id 获取实体。
   *
   * @param id - 实体 id
   * @returns 实体；未找到返回 undefined
   */
  getEntity(id: string): IEntity | undefined {
    return this.entitieMap.get(id)
  }

  /**
   * 局部更新绘图平面，缺省字段保持不变。
   *
   * @param plane - 绘图平面的部分字段
   */
  updatePlane(plane: Partial<IPlaneMetaData>) {
    const { origin, normal, up, elevation } = plane
    origin && this._plane.origin.copy(origin)
    normal && this._plane.normal.copy(normal)
    up && this._plane.up.copy(up)
    elevation !== undefined && (this._plane.elevation = elevation)
  }
}
